import React, { useEffect, useState } from 'react';
import { IUserProfile } from '../../models';
import InputComponent from '../input/input.component';
import * as yup from 'yup';
import Select, { IOption } from '../../select/select.component';
import ButtonSecondary from '../button-refactor/button-secondary';
import ButtonPrimary from '../button-refactor/button-primary';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { userService } from '../../services/user.service';
import toast from 'react-hot-toast';
import ErrorValidateComponent from '../error-validate/error-validate.component';
import ReactSelect, { MultiValue } from 'react-select';
import { fetchAllCategory } from '../../api';
import dayjs from 'dayjs';
import _ from 'lodash';
import { InputWarning } from '../inputwarning/input-warning.component';
import { useDarkMode } from '../../hooks';
import { apiErrorMessage } from '../../utils/api-error-message';

interface EditProfileGeneralProps {
  onClose: () => void;
  profile: IUserProfile;

  onUpdateSuccess: () => void;
}

const schema = yup.object().shape({
  // username: yup.string().required('Vui lòng nhập tên người dùng'),
  // email: yup.string().email('Email không hợp lệ').required('Vui lòng nhập email'),
  dob: yup.string().required('Ngày sinh không được bỏ trống'),
  sex: yup.string().required('Vui lòng nhập giới tính'),
  customSex: yup.string(),
  gender: yup.string(),
  // city: yup.string().required('Vui lòng nhập tên thành phố'),
  description: yup.string(), // mô tả không bắt buộc (không có dấu * → không cưỡng chế nhập)
  fullname: yup.string().required('Vui lòng nhập họ tên'),
  categoryFavorites: yup
    .array()
    .of(yup.string())
    .min(1, 'Vui lòng chọn sở thích')
    .required('Sở thích không được bỏ trống'),
  // Email là ô readOnly (lấy từ tài khoản) → KHÔNG bắt buộc, tránh kẹt form khi user đăng ký không có email.
  email: yup
    .string()
    .transform((v) => v || undefined)
    .email('Email không hợp lệ'),
});

interface IFormValues {
  // username: string;
  // email: string;
  dob: string;
  sex: string;
  customSex?: string;
  email?: string;
  phone?: string;
  city?: string;
  homeLand?: string;
  description?: string;
  fullname: string;
  categoryFavorites: any[];
  sexualOrientation?: number;
  gender?: string;
  marry?: number;
}

const options: IOption[] = [
  {
    label: 'Nam',
    value: 'male',
  },
  {
    label: 'Nữ',
    value: 'female',
  },
  {
    label: 'Tùy chọn',
    value: 'other',
  },
];

const optionsRelationship: IOption[] = [
  {
    label: 'Độc thân',
    value: 0,
  },
  {
    label: 'Đã kết hôn',
    value: 1,
  },
];

const optionsSexualOrientation: IOption[] = [
  {
    label: 'Nam',
    value: 0,
  },
  {
    label: 'Nữ',
    value: 1,
  },
  {
    label: 'Tùy chọn',
    value: 2,
  },
];

const EditProfileGeneral = ({
  onClose,
  profile,
  onUpdateSuccess,
}: EditProfileGeneralProps) => {
  const {
    dob,
    city,
    description,
    sex,
    username,
    email,
    fullname,
    categoryFavorites,
    sexualOrientation,
    marry,
    phone,
    homeLand,
  } = profile;
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [categoryOptions, setCategoryOptions] = useState<IOption[]>([]);
  const { isDarkMode } = useDarkMode();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
    setError,
    clearErrors,
  } = useForm<IFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      dob,
      description,
      sex,
      phone,
      email,
      city,
      homeLand,
      fullname,
      categoryFavorites: categoryFavorites?.map((item) => item?.categoryId),
      sexualOrientation,
      gender: 'ss',
    },
  });

  const fetchCategoryOptions = async () => {
    try {
      // Replace this URL with the actual endpoint from which you're fetching category options
      const response = await fetchAllCategory();
      const data: any[] = response.map((item: any) => ({
        value: item.id,
        label: item.name,
      }));
      setCategoryOptions(data);
      // Optionally, update form default values here if necessary
    } catch (error) {
      console.error('Failed to fetch category options:', error);
    }
  };

  // Fetch category options on component mount
  useEffect(() => {
    fetchCategoryOptions();
  }, []);

  useEffect(() => {
    if (profile) {
      reset({
        ...profile,
        categoryFavorites: _.map(
          profile.categoryFavorites,
          (item) => item.categoryId
        ),
        dob: dayjs(profile.dob).format('YYYY-MM-DD'),
      });
    }
  }, [profile, reset]);

  const onSubmit: SubmitHandler<IFormValues> = async (formValues) => {
    try {
      setLoading(true);
      const payload = {
        ...profile,
        ...formValues,
        email: profile.email,
        customSex: formValues.customSex || 'Chưa rõ',
        dob: dayjs(formValues.dob).format('YYYY-MM-DD'),
      };
      await userService.updateProfile(payload as IUserProfile);

      onUpdateSuccess();
      setVisible(false);
      toast.success('Cập nhật thông tin thành công');
    } catch (error) {
      // Hiện lý do thật từ server (trước đây nuốt lỗi nên không biết vì sao hỏng, sếp báo 08/10).
      toast.error(apiErrorMessage(error, 'Không thể cập nhật tài khoản của bạn'));
      setVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (event: any) => {
    const { value } = event.target;
    const date = new Date(value);
    if (date.getFullYear() > 9999) {
      const currentDob = dayjs(profile.dob)
        .set('year', 1999)
        .format('YYYY-MM-DD');
      setValue('dob', currentDob);
    }
  };

  return (
    <form className="flex flex-col w-full" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-6 p-6">
        <div className="flex w-full gap-5 max-md:flex-col">
          <div className="flex flex-col  w-full gap-5">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="fullname"
                className="flex items-center   text-sm text-slate-800 dark:text-slate-50 "
              >
                Họ Tên&nbsp;
                <InputWarning />
              </label>
              <div className="w-full">
                <InputComponent
                  className="h-12"
                  placeholder="Tên tài khoản"
                  id="fullname"
                  register={register('fullname')}
                />
                <ErrorValidateComponent visible={!!errors.fullname}>
                  {errors.fullname?.message}
                </ErrorValidateComponent>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="dob"
                className="flex items-center   text-sm text-slate-800 dark:text-slate-50 "
              >
                Ngày sinh&nbsp;
                <InputWarning />
              </label>
              <div className="w-full">
                <InputComponent
                  className="h-12"
                  type="date"
                  placeholder="Ngày sinh"
                  id="dob"
                  onChange={handleDateChange}
                  register={register('dob')}
                />
                <ErrorValidateComponent visible={!!errors.dob}>
                  {errors.dob?.message}
                </ErrorValidateComponent>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="city"
                className="flex items-center   text-sm text-slate-800 dark:text-slate-50 "
              >
                Nơi ở hiện tại&nbsp;
                <InputWarning />
              </label>
              <div className="w-full">
                <InputComponent
                  className="h-12"
                  placeholder="Thành phố"
                  id="city"
                  register={register('city')}
                />
                <ErrorValidateComponent visible={!!errors.city}>
                  {errors.city?.message}
                </ErrorValidateComponent>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="email"
                className=" flex items-center  text-sm text-slate-800 dark:text-slate-50 "
              >
                Email
              </label>
              <div className="w-full">
                <InputComponent
                  className="h-12"
                  placeholder="Email"
                  id="email"
                  register={register('email')}
                  readOnly
                />
                <ErrorValidateComponent visible={!!errors.email}>
                  {errors.email?.message}
                </ErrorValidateComponent>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="categoryFavorites"
                className="flex items-center   text-sm text-slate-800 dark:text-slate-50 "
              >
                Sở thích&nbsp;
                <InputWarning />
              </label>
              <div className="w-full">
                {categoryOptions.length > 0 && (
                  <ReactSelect
                    isMulti
                    options={categoryOptions}
                    onChange={(selectedOptions: MultiValue<any>) => {
                      setValue(
                        'categoryFavorites',
                        selectedOptions.map((option) => option.value)
                      );
                    }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '48px',
                        borderRadius: '8px',
                        backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                        borderColor: isDarkMode ? '#334155' : '#C8CCD8',
                        boxShadow: 'none',
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                        border: isDarkMode ? '1px solid #334155' : undefined,
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? isDarkMode
                            ? '#334155'
                            : '#F6F7F8'
                          : 'transparent',
                        color: isDarkMode ? '#f8fafc' : '#111827',
                        cursor: 'pointer',
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? '#334155' : '#e2e8f0',
                        borderRadius: '6px',
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: isDarkMode ? '#f8fafc' : '#111827',
                      }),
                      input: (base) => ({
                        ...base,
                        color: isDarkMode ? '#f8fafc' : '#111827',
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: '#94a3b8',
                      }),
                    }}
                    placeholder="Sở thích"
                    value={categoryOptions.filter((option) => {
                      if (watch('categoryFavorites')) {
                        return watch('categoryFavorites').includes(
                          option.value
                        );
                      }
                      return false;
                    })}
                  />
                )}
              </div>
              <ErrorValidateComponent visible={!!errors.categoryFavorites}>
                {errors.categoryFavorites?.message}
              </ErrorValidateComponent>
            </div>
          </div>
          <div className="flex flex-col  w-full gap-5">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="gender"
                className="flex items-center   text-sm text-slate-800 dark:text-slate-50 "
              >
                Giới tính&nbsp;
                <InputWarning />
              </label>
              <div className="w-full flex flex-col gap-2">
                <div className="font-normal">
                  <Select
                    options={options}
                    onChange={(data: any) => {
                      setValue('sex', data.value);
                    }}
                    placeholder="Giới tính"
                    value={watch('sex')}
                  />
                  <ErrorValidateComponent visible={!!errors.sex}>
                    {errors.sex?.message}
                  </ErrorValidateComponent>
                </div>
                {watch('sex') === 'other' && (
                  <InputComponent
                    placeholder="Nhập giới tính"
                    register={register('customSex')}
                    value={watch('customSex')?.replaceAll(/[0-9]/g, '')}
                  />
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="gender"
                className="flex items-center   text-sm text-slate-800 dark:text-slate-50 "
              >
                Tình trạng hôn nhân&nbsp;
                <InputWarning />
              </label>
              <div className="w-full flex flex-col gap-2">
                <Select
                  options={optionsRelationship}
                  onChange={(data: any) => {
                    setValue('marry', data.value);
                  }}
                  placeholder="Tình trạng hôn nhân"
                  value={watch('marry')}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="homeLand"
                className="flex items-center   text-sm text-slate-800 dark:text-slate-50 "
              >
                Quê quán&nbsp;
                <InputWarning />
              </label>
              <div className="w-full">
                <InputComponent
                  className="h-12"
                  placeholder="Quê quán"
                  id="homeLand"
                  register={register('homeLand')}
                />
                <ErrorValidateComponent visible={!!errors.homeLand}>
                  {errors.homeLand?.message}
                </ErrorValidateComponent>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="phone"
                className="flex items-center   text-sm text-slate-800 dark:text-slate-50 "
              >
                Điện thoại&nbsp;
                <InputWarning />
              </label>
              <div className="w-full">
                <InputComponent
                  className="h-12"
                  type="tel"
                  placeholder="Điện thoại"
                  id="phone"
                  register={register('phone')}
                />
                <ErrorValidateComponent visible={!!errors.phone}>
                  {errors.phone?.message}
                </ErrorValidateComponent>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="gender"
                className="flex items-center   text-sm text-slate-800 dark:text-slate-50 "
              >
                Bạn thích&nbsp;
                <InputWarning />
              </label>
              <div className="w-full flex flex-col gap-2">
                <Select
                  options={optionsSexualOrientation}
                  onChange={(data: any) => {
                    setValue('sexualOrientation', data.value);
                  }}
                  placeholder="Giới tính"
                  value={watch('sexualOrientation')}
                />
                {watch('sexualOrientation') === 2 && (
                  <InputComponent
                    placeholder="Nhập giới tính"
                    register={register('gender')}
                    value={watch('gender')?.replaceAll(/[0-9]/g, '')}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="description"
            className="flex items-center   text-sm text-slate-800 dark:text-slate-50 "
          >
            Mô tả
          </label>
          <div className="w-full">
            <InputComponent
              type="textarea"
              placeholder="Mô tả"
              id="description"
              register={register('description')}
            />
            <ErrorValidateComponent visible={!!errors.description}>
              {errors.description?.message}
            </ErrorValidateComponent>
          </div>
        </div>
      </div>
      <div className="flex justify-end items-center gap-3 border-t border-t-[#DEE0E6] dark:border-t-slate-700 px-6 py-4">
        <ButtonSecondary
          className="min-w-[112px] h-11 flex items-center justify-center !bg-transparent hover:!bg-slate-100 dark:hover:!bg-slate-700 !text-slate-600 dark:!text-slate-200 !border-[#DEE0E6] dark:!border-slate-600 active:scale-[0.98] transition"
          onClick={onClose}
        >
          Hủy bỏ
        </ButtonSecondary>
        <ButtonPrimary
          loading={loading}
          className="min-w-[132px] h-11 justify-center active:scale-[0.98] transition"
        >
          Cập nhật
        </ButtonPrimary>
      </div>
    </form>
  );
};

export default EditProfileGeneral;
